import React, { FunctionComponent } from "react";
import Head from "next/head";
import Link from "next/link";

import Banner from "src/components/Banner";
import CoreLogo from "src/components/logos/CoreLogo";
import EngineLogo from "src/components/logos/EngineLogo";
import CloudLogo from "src/components/logos/CloudLogo";
import IndexLogo from "src/components/logos/IndexLogo";
import OpenInNewIcon from "src/components/icons/OpenInNewIcon";
import { BlockProtocolIcon } from "src/components/icons/BlockProtocolIcon";

const blueH = <span className="bright-blue">h</span>;

const diagramAltText =
  "Diagram shows how structured data is created using interactive Block Protocol blocks in the HASH workspace, and insights generated from the hEngine simulation engine.";

const Platform: FunctionComponent = () => (
  <>
    <Head>
      <title>Platform - HASH</title>
    </Head>
    <div className="platform-page platform-overview">
      <div className="mainsite-header">
        <div className="container">
          <h1 className="heading-1">Platform</h1>
          <p className="large-text">
            The <strong>HASH</strong> platform provides an end-to-end solution
            for safely automating decision-making
          </p>
        </div>
      </div>

      <div className="platform-content">
        <div className="container">
          <h3 className="heading-4">DATA AND KNOWLEDGE MANAGEMENT</h3>

          <div className="paras">
            <div className="para index">
              <div className="para-image">
                <IndexLogo />
              </div>
              <div className="para-text">
                <h4>HASH</h4>
                <p>
                  The all-in-one, open-source workspace for building entity
                  graphs, datasets and simulations
                </p>
                <p>
                  <Link href="/platform/hash">
                    <a>Find out more{" >"}</a>
                  </Link>
                </p>
              </div>
            </div>

            <div className="para blockprotocol">
              <div className="para-image">
                <BlockProtocolIcon />
              </div>
              <div className="para-text">
                <div className="engine-header">
                  <h4>Block Protocol</h4>
                </div>
                <p>
                  An open-standard for developers creating rich, content-driven
                  applications
                </p>
                <p>
                  <a href="https://blockprotocol.org" target="_blank">
                    Visit blockprotocol.org <OpenInNewIcon />
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <h3 className="heading-4">SIMULATION AND COMPUTE</h3>

          <div className="paras">
            <div className="para core">
              <div className="para-image">
                <CoreLogo />
              </div>
              <div className="para-text">
                <h4>{blueH}Core</h4>
                <p>
                  Browser-based tool for connecting data and quickly creating,
                  visualizing and exploring simulations
                </p>
                <p>
                  <Link href="/platform/core">
                    <a>Get building{" >"}</a>
                  </Link>
                </p>
              </div>
            </div>

            <div className="para cloud">
              <div className="para-image">
                <CloudLogo />
              </div>
              <div className="para-text">
                <h4>{blueH}Cloud</h4>
                <p>
                  Automatically optimize and efficiently distribute simulations
                  with zero effort, setup, or additional code
                </p>
                <p>
                  <Link href="/platform/cloud" prefetch={false}>
                    <a>View free tier and pricing{" >"}</a>
                  </Link>
                </p>
              </div>
            </div>

            <div className="para engine">
              <div className="para-image">
                <EngineLogo />
              </div>
              <div className="para-text">
                <div className="engine-header">
                  <h4>{blueH}Engine</h4>
                  <span className="open-badge">Available on GitHub</span>
                </div>
                <p>
                  Open, massively scalable multi-agent simulation engine at the
                  heart of HASH
                </p>
                <p>
                  <Link href="/platform/engine" prefetch={false}>
                    <a>{"Get hEngine on GitHub>"}</a>
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="how-it-works">
        <h3 className="container heading-4">
          <span>{"HOW IT WORKS:"}&nbsp;</span>
          <span className="light">{"INSIGHT MADE EASY"}</span>
        </h3>

        <div className="platform-diagram">
          <img
            src="https://cdn-us1.hash.ai/assets/platform-connections-full.svg"
            className="diagram-full"
            alt={diagramAltText}
          />

          <div className="container">
            <img
              src="https://cdn-us1.hash.ai/assets/platform-connections-medium.svg"
              className="diagram-medium"
              alt={diagramAltText}
            />
          </div>

          <div className="container">
            <img
              src="https://cdn-us1.hash.ai/assets/platform-connections-responsive.svg"
              className="diagram-responsive"
              alt={diagramAltText}
            />
          </div>
        </div>
      </div>

      <Banner
        variant="bright-blue"
        headingSmall="More Features? Special Requests?"
        headingBig="Discover Enterprise"
        href="/pricing"
      />
      <Banner
        variant="black-almost"
        headingSmall="See Our Upcoming Features"
        headingBig="Product Roadmap"
        href="/roadmap"
      />
    </div>
  </>
);

export default Platform;
